import React, { useEffect, useState } from 'react';
import { Button } from '@chakra-ui/react';
import MuiSelect from 'components/MuiSelect';
import MuiAutocomplete from 'components/MuiAutocomplete';
import { useTranslation } from 'react-i18next';

const GEO_SCOPE_LEVELS = {
  REGIONAL: 'REGIONAL',
  DEPARTMENTAL: 'DEPARTMENTAL',
  ARRONDISSEMENTAL: 'ARRONDISSEMENTAL',
  UNRESTRICTED: 'UNRESTRICTED',
};

const GeoScopeManager = ({
  regions,
  departments,
  arrondissements,
  selectedRegions,
  selectedDepartments,
  selectedArrondissements,
  onRegionChange,
  onDepartmentChange,
  onArrondissementChange,
  onUpdateGeoScope,
  onRemoveAllGeoScope,
  canEdit,
  loadingGeoUpdate,
  loadingRemoveAll,
  user,
}) => {
  const { t } = useTranslation('update_user_informations');
  const [scopeLevel, setScopeLevel] = useState('');
  useEffect(() => {
    setScopeLevel(user?.scopeLevel || '');
  }, [user?.scopeLevel]);
  const handleUpdateGeoScope = async () => {
    if (!scopeLevel) {
      alert(t('error_select_scope_level') || 'Please select a scope level');
      return;
    }

    // Prepare data based on scope level
    // The user can filter through all levels, but payload is built based on selected scope
    let geoData = {
      scopeLevel: scopeLevel,
    };

    if (scopeLevel === GEO_SCOPE_LEVELS.REGIONAL) {
      // Send only region IDs for REGIONAL scope
      geoData.regionIds = selectedRegions?.length > 0 ? selectedRegions : null;
      geoData.departmentIds = null;
      geoData.arrondissementIds = null;
    } else if (scopeLevel === GEO_SCOPE_LEVELS.DEPARTMENTAL) {
      // Send only department IDs for DEPARTMENTAL scope
      geoData.regionIds = null;
      geoData.departmentIds = selectedDepartments?.length > 0 ? selectedDepartments : null;
      geoData.arrondissementIds = null;
    } else if (scopeLevel === GEO_SCOPE_LEVELS.ARRONDISSEMENTAL) {
      // Send only arrondissement IDs for ARRONDISSEMENTAL scope
      geoData.regionIds = null;
      geoData.departmentIds = null;
      geoData.arrondissementIds =
        selectedArrondissements?.length > 0 ? selectedArrondissements : null;
    } else if (scopeLevel === GEO_SCOPE_LEVELS.UNRESTRICTED) {
      // No restrictions
      geoData.regionIds = null;
      geoData.departmentIds = null;
      geoData.arrondissementIds = null;
    }

    await onUpdateGeoScope(geoData);
  };

  return (
    <div className="pl-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-green-500">
          {t('geographic_scope') || 'Geographic Scope'}
        </h3>
      </div>

      {/* Scope Level Selection */}
      {
        <div className="my-4 mb-6">
          <MuiSelect
            disabled={!canEdit}
            displayOnly={!canEdit}
            label={t('select_scope_level') || 'Select Scope Level'}
            options={[
              {
                value: GEO_SCOPE_LEVELS.UNRESTRICTED,
                label: t('scope_level_unrestricted') || 'Unrestricted',
              },
              { value: GEO_SCOPE_LEVELS.REGIONAL, label: t('scope_level_regional') || 'Regional' },
              {
                value: GEO_SCOPE_LEVELS.DEPARTMENTAL,
                label: t('scope_level_depart') || 'Departmental',
              },
              {
                value: GEO_SCOPE_LEVELS.ARRONDISSEMENTAL,
                label: t('scope_level_arrondissement') || 'Arrondissement',
              },
            ]}
            value={scopeLevel}
            onChange={(e) => setScopeLevel(e.target.value)}
            emptyLabel={t('option_none') || 'None'}
            wrapperClasses={'w-full'}
          />
        </div>
      }

      {/* Filtering section - Display fields based on scope level */}
      {scopeLevel !== GEO_SCOPE_LEVELS.UNRESTRICTED && (
        <div className="my-4 grid gap-5 md:grid-cols-1">
          {/* Regions - Visible for REGIONAL, DEPARTMENTAL, ARRONDISSEMENTAL scopes */}
          {(scopeLevel === GEO_SCOPE_LEVELS.REGIONAL ||
            scopeLevel === GEO_SCOPE_LEVELS.DEPARTMENTAL ||
            scopeLevel === GEO_SCOPE_LEVELS.ARRONDISSEMENTAL) && (
            <div>
              <MuiAutocomplete
                key={`regions-${selectedRegions?.join(',')}`}
                disabled={!canEdit}
                displayOnly={!canEdit}
                value={
                  regions?.data && Array.isArray(regions.data)
                    ? regions.data
                        .filter((r) => selectedRegions?.includes(r.id))
                        .map((r) => ({ value: r.id, label: r.name }))
                    : []
                }
                label={t('select_regions') || 'Select Regions'}
                options={Array.isArray(regions?.data) ? regions.data : []}
                onChange={onRegionChange}
                multiple={true}
                dataCy="select-regions"
                defaultFormat={true}
              />
            </div>
          )}

          {/* Departments - Visible only for DEPARTMENTAL and ARRONDISSEMENTAL scopes (cascaded from regions) */}
          {(scopeLevel === GEO_SCOPE_LEVELS.DEPARTMENTAL ||
            scopeLevel === GEO_SCOPE_LEVELS.ARRONDISSEMENTAL) &&
            selectedRegions?.length > 0 && (
              <div key={`departments-container-${selectedRegions?.join(',')}`}>
                <MuiAutocomplete
                  key={`departments-${selectedDepartments?.join(',')}`}
                  disabled={!canEdit}
                  displayOnly={!canEdit}
                  value={
                    departments?.data && Array.isArray(departments.data)
                      ? departments.data
                          .filter((d) => selectedDepartments?.includes(d.id))
                          .map((d) => ({ value: d.id, label: d.name }))
                      : []
                  }
                  label={t('select_departments') || 'Select Departments'}
                  options={Array.isArray(departments?.data) ? departments.data : []}
                  onChange={onDepartmentChange}
                  multiple={true}
                  dataCy="select-departments"
                  defaultFormat={true}
                />
              </div>
            )}

          {/* Arrondissements - Visible only for ARRONDISSEMENTAL scope (cascaded from departments) */}
          {scopeLevel === GEO_SCOPE_LEVELS.ARRONDISSEMENTAL && selectedDepartments?.length > 0 && (
            <div key={`arrondissements-container-${selectedDepartments?.join(',')}`}>
              <MuiAutocomplete
                key={`arrondissements-${selectedArrondissements?.join(',')}`}
                disabled={!canEdit}
                displayOnly={!canEdit}
                value={
                  arrondissements?.data && Array.isArray(arrondissements.data)
                    ? arrondissements.data
                        .filter((a) => selectedArrondissements?.includes(a.id))
                        .map((a) => ({ value: a.id, label: a.name }))
                    : []
                }
                label={t('select_arrondissements') || 'Select Arrondissements'}
                options={Array.isArray(arrondissements?.data) ? arrondissements.data : []}
                onChange={onArrondissementChange}
                multiple={true}
                dataCy="select-arrondissements"
                defaultFormat={true}
              />
            </div>
          )}
        </div>
      )}

      {/* Summary of selection */}
      {canEdit && scopeLevel && (
        <div
          className={`my-4 p-4 rounded-md border ${
            scopeLevel === GEO_SCOPE_LEVELS.UNRESTRICTED
              ? 'bg-green-50 border-green-200'
              : 'bg-blue-50 border-blue-200'
          }`}
        >
          <p
            className={`text-sm font-semibold mb-2 ${
              scopeLevel === GEO_SCOPE_LEVELS.UNRESTRICTED ? 'text-green-800' : 'text-blue-800'
            }`}
          >
            {t('geo_scope_summary') || 'Summary'}:
          </p>

          {scopeLevel === GEO_SCOPE_LEVELS.UNRESTRICTED && (
            <div className="flex items-center gap-2">
              <span className="text-lg">🌍</span>
              <p className="text-sm text-green-700 font-semibold">
                {t('scope_access_unrestricted') || 'Full Access - No geographic restrictions'}
              </p>
            </div>
          )}

          {scopeLevel === GEO_SCOPE_LEVELS.REGIONAL && (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-lg">📍</span>
                <p className="text-sm text-blue-700 font-semibold">
                  {t('scope_restricted_to') || 'Restricted to'}: {selectedRegions?.length || 0}{' '}
                  region(s)
                </p>
              </div>
              {selectedRegions?.length > 0 && (
                <div className="ml-6 text-xs text-blue-600">
                  {regions?.data
                    ?.filter((r) => selectedRegions?.includes(r.id))
                    .map((r) => r.name)
                    .join(', ')}
                </div>
              )}
            </div>
          )}

          {scopeLevel === GEO_SCOPE_LEVELS.DEPARTMENTAL && (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-lg">🏢</span>
                <p className="text-sm text-blue-700 font-semibold">
                  {t('scope_restricted_to') || 'Restricted to'}: {selectedDepartments?.length || 0}{' '}
                  department(s)
                </p>
              </div>
              {selectedDepartments?.length > 0 && (
                <div className="ml-6 text-xs text-blue-600">
                  {departments?.data
                    ?.filter((d) => selectedDepartments?.includes(d.id))
                    .map((d) => d.name)
                    .join(', ')}
                </div>
              )}
            </div>
          )}

          {scopeLevel === GEO_SCOPE_LEVELS.ARRONDISSEMENTAL && (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-lg">🎯</span>
                <p className="text-sm text-blue-700 font-semibold">
                  {t('scope_restricted_to') || 'Restricted to'}:{' '}
                  {selectedArrondissements?.length || 0} arrondissement(s)
                </p>
              </div>
              {selectedArrondissements?.length > 0 && (
                <div className="ml-6 text-xs text-blue-600">
                  {arrondissements?.data
                    ?.filter((a) => selectedArrondissements?.includes(a.id))
                    .map((a) => a.name)
                    .join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {canEdit && (
        <div className="flex flex-row gap-3 mt-5">
          <Button
            size="sm"
            variant="success"
            onClick={handleUpdateGeoScope}
            isLoading={loadingGeoUpdate}
            width="100%"
          >
            {t('save_geo_scope') || 'Save Geographic Scope'}
          </Button>
          <Button
            size="sm"
            variant="danger"
            className="btn-danger"
            onClick={onRemoveAllGeoScope}
            isLoading={loadingRemoveAll}
            width="100%"
          >
            {t('remove_all_allowed') || 'Remove All Allowed'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default GeoScopeManager;
